import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  blogApi,
  metaApi,
  contactApi,
  jobsApi,
  plansApi,
  projectsApi,
  searchApi,
  servicesApi,
  turnkeyApi,
} from "@/api";
import type { BlogQuery, JobQuery, PlanQuery, ProjectQuery, SearchQuery, TurnkeyQuery } from "@/api/queries";
import { qk } from "@/lib/queryClient";
import { useLicenceStore } from "@/store/licences";

/* -------------------------------------------------------------------------- */
/* Data                                                                       */
/* -------------------------------------------------------------------------- */

export function useProjects(query: ProjectQuery = {}) {
  return useQuery({
    queryKey: qk.projects(query),
    queryFn: () => projectsApi.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: qk.project(id ?? ""),
    queryFn: () => projectsApi.byId(id!),
    enabled: Boolean(id),
  });
}

export function useTurnkeyProjects(query: TurnkeyQuery = {}) {
  return useQuery({
    queryKey: qk.turnkey(query),
    queryFn: () => turnkeyApi.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useTurnkeyProject(id: string | undefined) {
  return useQuery({
    queryKey: qk.turnkeyItem(id ?? ""),
    queryFn: () => turnkeyApi.byId(id!),
    enabled: Boolean(id),
  });
}

export function usePlans(query: PlanQuery = {}) {
  return useQuery({
    queryKey: qk.plans(query),
    queryFn: () => plansApi.list(query),
    placeholderData: keepPreviousData,
  });
}

export function usePlan(id: string | undefined) {
  // The cached licence is sent with the request so the server can decide
  // whether to return the full drawing. Its absence simply yields the locked
  // payload — the decision is never made here.
  const licence = useLicenceStore((state) => (id ? state.licences[id] : undefined));

  return useQuery({
    queryKey: qk.plan(id ?? "", licence?.licenceId),
    queryFn: () => plansApi.byId(id!, licence),
    enabled: Boolean(id),
  });
}

export function useBlogPosts(query: BlogQuery = {}) {
  return useQuery({
    queryKey: qk.blog(query),
    queryFn: () => blogApi.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useBlogPost(id: string | undefined) {
  return useQuery({
    queryKey: qk.blogPost(id ?? ""),
    queryFn: () => blogApi.byId(id!),
    enabled: Boolean(id),
  });
}

export function useServices() {
  return useQuery({ queryKey: qk.services(), queryFn: () => servicesApi.list() });
}

export function useService(id: string | undefined) {
  return useQuery({
    queryKey: qk.service(id ?? ""),
    queryFn: () => servicesApi.byId(id!),
    enabled: Boolean(id),
  });
}

export function useJobs(query: JobQuery = {}) {
  return useQuery({
    queryKey: qk.jobs(query),
    queryFn: () => jobsApi.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: qk.job(id ?? ""),
    queryFn: () => jobsApi.byId(id!),
    enabled: Boolean(id),
  });
}

/** Facets change only when the catalogue does, so they never go stale in-session. */
export function useFacets() {
  return useQuery({
    queryKey: ["facets"],
    queryFn: () => metaApi.facets(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useSearch(query: SearchQuery = {}) {
  return useQuery({
    queryKey: qk.search(query),
    queryFn: () => searchApi.query(query),
    placeholderData: keepPreviousData,
  });
}

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

export function useEnquiryMutation() {
  return useMutation({ mutationFn: contactApi.enquire });
}

export function useApplicationMutation() {
  return useMutation({ mutationFn: jobsApi.apply });
}

export function usePurchaseMutation() {
  return useMutation({ mutationFn: plansApi.purchase });
}

export function useSubscribeMutation() {
  return useMutation({ mutationFn: contactApi.subscribe });
}

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

export function useDebounced<T>(value: T, delay = 220): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    // Only correct a drift between render and subscribe; no unconditional set.
    setMatches((current) => (current === list.matches ? current : list.matches));
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** True once the user has scrolled past `offset` — drives the sticky header. */
export function useScrolled(offset = 18): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);

  return scrolled;
}

/**
 * Filter state mirrored into the URL, so a filtered view can be shared,
 * bookmarked and restored by the back button.
 */
export function useUrlFilters<T extends Record<string, string | string[] | number | boolean>>(
  defaults: T,
) {
  const [params, setParams] = useSearchParams();

  const value = useMemo(() => {
    const next = { ...defaults };
    for (const key of Object.keys(defaults) as (keyof T & string)[]) {
      const fallback = defaults[key];
      if (Array.isArray(fallback)) {
        const all = params.getAll(key);
        if (all.length) next[key] = all as T[keyof T & string];
      } else if (typeof fallback === "number") {
        const raw = params.get(key);
        if (raw !== null && raw !== "" && Number.isFinite(Number(raw))) {
          next[key] = Number(raw) as T[keyof T & string];
        }
      } else if (typeof fallback === "boolean") {
        const raw = params.get(key);
        if (raw !== null) next[key] = (raw === "true") as T[keyof T & string];
      } else {
        const raw = params.get(key);
        if (raw !== null) next[key] = raw as T[keyof T & string];
      }
    }
    return next;
    // params.toString() gives a stable dependency for the whole query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const set = useCallback(
    (patch: Partial<T>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, entry] of Object.entries(patch)) {
            next.delete(key);
            const fallback = defaults[key as keyof T];

            if (Array.isArray(entry)) {
              for (const v of entry) next.append(key, String(v));
            } else if (entry !== undefined && entry !== null && entry !== "" && entry !== fallback) {
              next.set(key, String(entry));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams, defaults],
  );

  const reset = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);

  return { value, set, reset };
}

/** Tracks whether an element is being pointer-dragged, for pan surfaces. */
export function usePanState() {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const origin = useRef({ x: 0, y: 0 });

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      setDragging(true);
      origin.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [pan.x, pan.y],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!dragging) return;
      setPan({ x: event.clientX - origin.current.x, y: event.clientY - origin.current.y });
    },
    [dragging],
  );

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    setDragging(false);
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* pointer already released */
    }
  }, []);

  const reset = useCallback(() => setPan({ x: 0, y: 0 }), []);

  return { pan, dragging, reset, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
}
